import { AGENT_LIMITS } from '@hookpipe/contracts/flows/agents/agent-config.schema';
import {
  ENodeType,
  FLOW_GRAPH_SCHEMA_VERSION,
} from '@hookpipe/contracts/flows/constants/flow.constants';
import {
  FlowGraphSchema,
  type TFlowGraph,
} from '@hookpipe/contracts/flows/schemas/flow-graph.schema';

import type { components } from '../../shared/api/schema.gen';

type TFlowGraphDto = components['schemas']['FlowGraphDto'];

export const CODE_STARTER = [
  'export default (event, ctx) => {',
  '  // event: what reached this step. ctx.nodes.<key>: what earlier steps produced.',
  '  return event;',
  '};',
  '',
].join('\n');

// A new Code step starts with its own code (§5.1); a new Merge combines answers, not events —
// an absent `combines` means the old way, which published flows keep (§4.5). A new agent
// writes the platform's limits down, so the panel shows what the run will obey.
export const starterConfig = (type: string): Record<string, unknown> => {
  if (type === ENodeType.CODE) return { source: CODE_STARTER };
  if (type === ENodeType.MERGE) return { combines: 'results' };
  if (type === ENodeType.AI_AGENT) {
    return {
      tools: [],
      limits: {
        maxSteps: AGENT_LIMITS.defaultSteps,
        maxCostUsd: AGENT_LIMITS.defaultCostUsd,
        maxActiveSec: AGENT_LIMITS.defaultActiveSec,
      },
      output: { kind: 'text' },
    };
  }
  return {};
};

// Parsed rather than asserted: a graph the API sends in another shape shows as unreadable.
export const parseGraph = (graph: TFlowGraphDto | null): TFlowGraph | null => {
  if (!graph) return null;
  const parsed = FlowGraphSchema.safeParse(graph);
  return parsed.success ? parsed.data : null;
};

export const oneStepGraph = (
  sourceId: string,
  sourceName: string,
  destinationId: string,
  destinationName: string,
): TFlowGraph => ({
  schema: FLOW_GRAPH_SCHEMA_VERSION,
  nodes: [
    {
      key: 'n_trigger',
      type: ENodeType.TRIGGER_WEBHOOK,
      name: sourceName.slice(0, 80) || 'Source',
      config: { sourceId },
      position: [360, 120],
    },
    {
      key: 'n_deliver',
      type: ENodeType.DELIVER,
      name: destinationName.slice(0, 80) || 'Destination',
      config: { destinationId },
      position: [360, 260],
    },
  ],
  edges: [{ from: 'n_trigger', output: 'main', to: 'n_deliver', input: 'main' }],
});

export type TTriggerChoice = 'webhook' | 'schedule' | 'manual';

export const TRIGGER_TYPE: Record<TTriggerChoice, string> = {
  webhook: ENodeType.TRIGGER_WEBHOOK,
  schedule: ENodeType.TRIGGER_SCHEDULE,
  manual: ENodeType.TRIGGER_MANUAL,
};

export const triggerChoiceOf = (type: string | undefined): TTriggerChoice | null =>
  (Object.keys(TRIGGER_TYPE) as TTriggerChoice[]).find((kind) => TRIGGER_TYPE[kind] === type) ??
  null;

export const blankGraph = (): TFlowGraph => ({
  schema: FLOW_GRAPH_SCHEMA_VERSION,
  nodes: [],
  edges: [],
});

const UNTITLED = /^Untitled flow(?: (\d+))?$/;

export const untitledFlowName = (taken: readonly string[]): string => {
  const used = taken.flatMap((name) => {
    const match = UNTITLED.exec(name.trim());
    return match ? [match[1] ? Number(match[1]) : 1] : [];
  });
  if (used.length === 0) return 'Untitled flow';
  return `Untitled flow ${Math.max(...used) + 1}`;
};
